import { memo, useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import { Avatar, AvatarFallback, AvatarImage, PrimaryLink, Text } from '@/_components';
import { DeleteButton, SortableList } from '@/_containers';
import type { InventoryQuerySchema } from '@/_pages/settings/inventory-kits/shared/schemas/inventory';

type InventoryItemProps = {
  inventory: InventoryQuerySchema;
  isEditing?: boolean;
  sortable?: boolean;
  onDelete: (inventory: InventoryQuerySchema) => void;
  className?: string;
};

function InventoryItemBase({
  inventory,
  isEditing = false,
  onDelete,
  className,
  sortable = false,
}: InventoryItemProps) {
  const BaseItem = useMemo(
    () => (
      <section
        className={twMerge(
          'border-c-border-muted text-accent flex min-h-11 flex-1 flex-col flex-nowrap items-center justify-between gap-1.5 border-b py-2 sm:flex-row dark:text-white',
          className
        )}
      >
        <div className="flex w-full items-center gap-4">
          <PrimaryLink to="/inventory/$id" params={{ id: `${inventory.id}` }} state={{ naturalBack: true }}>
            <Text className="text-accent min-w-20 dark:text-white">{inventory.primary_sku || '—'}</Text>
          </PrimaryLink>
          <div className="flex items-center">
            <Link to="/inventory/$id" params={{ id: `${inventory.id}` }} state={{ naturalBack: true }}>
              <Avatar size="sm" className="rounded-md">
                <AvatarImage src={inventory.logo?.at(0)?.url} className="object-cover" />
                <AvatarFallback name={inventory.name} className="text-xs hover:no-underline" />
              </Avatar>
            </Link>
            <PrimaryLink to="/inventory/$id" params={{ id: `${inventory.id}` }} state={{ naturalBack: true }}>
              <div className="text-accent pl-3 text-sm font-normal dark:text-white">{inventory?.name ?? ''}</div>
            </PrimaryLink>
          </div>
        </div>

        {isEditing && (
          <DeleteButton
            type="icon"
            size="sm"
            title="Remove Inventory?"
            text="The Inventory record will not be deleted; only its association will be removed."
            yesButton="Yes, Remove"
            aria-label="delete-inventory"
            callback={() => onDelete(inventory)}
          />
        )}
      </section>
    ),
    [className, inventory, isEditing, onDelete]
  );

  return sortable ? (
    <SortableList.Item key={inventory.id} id={inventory.id} className="items-center!">
      {isEditing && <SortableList.DragHandle className="mr-1!" />}
      {BaseItem}
    </SortableList.Item>
  ) : (
    BaseItem
  );
}

export const Item = memo(InventoryItemBase);
